import { motion } from "framer-motion";
import { PRODUCTS, WHATSAPP_LINK } from "../../lib/constants";
import { Plus, ExternalLink, Star } from "lucide-react";

const ProductsSection = ({ onAddToCart }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section
      id="productos"
      className="py-20 md:py-32 bg-[#F4EDE4]"
      data-testid="products-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider">
            Nuestro Menú
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 mb-4">
            ¿Cuál será tu <span className="italic text-[#C88037]">perdición favorita?</span>
          </h2>
          <p className="text-[#5E4B3C] max-w-2xl mx-auto">
            Cada brownie es una pequeña obra de arte comestible. 
            Pero cuidado... una vez que pruebes uno, querrás probarlos todos.
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {PRODUCTS.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-[#FDFBF7] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                {/* Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Tag */}
                  {product.tag && (
                    <span className="absolute top-4 left-4 bg-[#C88037] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {product.tag}
                    </span>
                  )}
                  {/* Quick add overlay */}
                  <div className="absolute inset-0 bg-[#2D1810]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onAddToCart(product)}
                      className="bg-[#C88037] text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2"
                      data-testid={`add-product-${product.id}`}
                    >
                      <Plus className="w-5 h-5" strokeWidth={1.5} />
                      Agregar
                    </motion.button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-serif text-xl text-[#2D1810] font-semibold">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-1 text-[#C88037]">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm font-medium">5.0</span>
                    </div>
                  </div>
                  <p className="text-[#5E4B3C] text-sm flex-1 mb-4">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-[#C88037] font-bold text-lg">
                      {product.priceRange}
                    </span>
                    <button
                      onClick={() => onAddToCart(product)}
                      className="p-2 bg-[#F4EDE4] hover:bg-[#C88037] hover:text-white rounded-full transition-colors"
                      data-testid={`add-btn-${product.id}`}
                    >
                      <Plus className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-[#5E4B3C] mb-4">
            ¿No encuentras lo que buscas? Tenemos más opciones esperándote.
          </p>
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#2D1810] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#422418] transition-colors"
            data-testid="view-full-menu"
          >
            <span>Ver Menú Completo</span>
            <ExternalLink className="w-5 h-5" strokeWidth={1.5} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;
